import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import SongTweaks from '../components/SongTweaks';
import axios from 'axios';
import toast from 'react-hot-toast';
import Votestars from '../components/Votestars';

export default function SongPage() {
    const { id } = useParams();
    const [song, setSong] = useState(null);
    const [loading, setLoading] = useState(true);
    const [commentText, setCommentText] = useState('');
    const [showVotes, setShowVotes] = useState(false);
    const [showRequests, setShowRequests] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const { authToken, user, removeVoteForSong, voteForSong, votes, favorites, isFavorited, toggleFavorite } = useAuth(); // add voteForSong, votes
    const [queuedNow, setQueuedNow] = useState(false);
    const [userVote, setUserVote] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        if (authToken) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    }, [authToken]);

    useEffect(() => {
        // Scroll to top when song changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [id]);

    useEffect(() => {
        fetchSong();
    }, [id]);

    useEffect(() => {
        if (votes && votes[id]) setUserVote(votes[id]);
    }, [authToken, id, votes]);

    const fetchSong = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`/api/song/${id}`);
            console.log('Fetched song data:', data);
            console.log('Tags:', data.tags);
            console.log('Related tags:', data.relatedTags);
            setSong(data);
        } catch (err) {
            console.error('Failed to fetch song:', err);
        } finally {
            setLoading(false);
        }
    };

    const submitComment = async () => {
        if (!commentText.trim()) return;
        try {
            await axios.post(`/api/song/${id}/comment`, { comment: commentText });
            setCommentText('');
            fetchSong(); // Refresh comments
        } catch (err) {
            toast.error('❌ Failed to post comment.');
            console.error('Failed to post comment:', err);
        }
    };

    const requestSong = async () => {
        try {
            setRequesting(true);
            const res = await axios.post(`/api/song/${id}`);
            if (res.status === 200) {
                toast.success('✅ Song successfully queued!');
                setQueuedNow(true);
                fetchSong(); // Refresh request history
            } else {
                toast.error('❌ Unexpected response from server');
            }
        } catch (err) {
            if (err.response?.status === 429) {
                toast.error('⏱️ You’re queuing too fast. Wait a moment.');
            } else if (err.response?.status === 403) {
                toast.error('⛔ You already have 2 songs in the queue.');
            } else if (err.response?.status === 409) {
                toast.error('📻 This song was played recently.');
            } else {
                toast.error('❌ Failed to request song.');
            }
        } finally {
            setRequesting(false);
        }
    };

    const removeVote = async () => {
    try {
        await axios.delete(`/api/song/${id}/vote`);
        await removeVoteForSong(Number(id)); // Sync local vote cache
        toast.success('🗑️ Vote removed');
        fetchSong(); // Refresh server-side data
    } catch (err) {
        toast.error('❌ Failed to remove vote');
    }
};

    const handleVote = async (songId, vote) => {
        try {
            if (userVote === vote) {
                await axios.delete(`/api/song/${songId}/vote`);
                await removeVoteForSong(Number(songId));
                setUserVote(null);
                toast.success('🗑️ Vote removed');
            } else {
                await voteForSong(Number(songId), vote);
                setUserVote(vote);
                toast.success(`✅ Voted ${vote}`);
            }
            fetchSong();
        } catch (err) {
            toast.error('❌ Failed to update vote');
        }
    };

    if (loading) return <p className="p-4">Loading...</p>;
    if (!song) return <p className="p-4">Song not found.</p>;

    const avgVote =
        song.votes.length > 0
            ? song.votes.reduce((sum, v) => sum + v.vote, 0) / song.votes.length
            : null;

    return (
        <div className="p-4 max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
                {song.song.title}
                {user && (
                    <button
                        onClick={() => toggleFavorite(song.song.id)}
                        className="text-xl"
                        title={isFavorited(song.song.id) ? 'Unfavorite' : 'Favorite'}
                    >
                        <img
                            src={isFavorited(song.song.id) ? '/emoji/2764.svg' : '/emoji/1F90D.svg'}
                            alt={isFavorited(song.song.id) ? 'Favorited' : 'Not favorited'}
                            className="inline w-6 h-6 align-text-bottom"
                            draggable={false}
                        />
                    </button>
                )}
            </h1>
            <p className="text-gray-600 mb-2">
                Artist:
                {song.song.artists
                    ? Object.entries(song.song.artists).map(([artistId, artistName], i, arr) => (
                        <span key={artistId}>
                            <Link to={`/artist/${artistId}`} className="text-blue-700 hover:underline">
                                {artistName}
                            </Link>
                            {i < arr.length - 1 && ', '}
                        </span>
                    ))
                    : <span className="text-gray-400">Unknown</span>
                }
                • Platform:{' '}
                <Link
                    to={`/platform/${encodeURIComponent(song.song.platform)}`}
                    className="text-blue-700 hover:underline"
                >
                    {song.song.platform}
                </Link>
            </p>
            
            {/* Tags */}
            {song.tags && song.tags.length > 0 && (
                <p className="text-gray-600 mb-2">
                    <img src="/emoji/1F3F7.svg" alt="Tags" className="inline w-5 h-5 mr-1 align-text-bottom" />
                    Tags:
                    <span className="flex flex-wrap gap-1 mt-1">
                        {song.tags.map((tag) => (
                            <Link
                                key={tag}
                                to={`/tags/${encodeURIComponent(tag)}`}
                                className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 rounded-md text-sm hover:bg-blue-200"
                            >
                                {tag}
                            </Link>
                        ))}
                    </span>
                </p>
            )}
            
            {/* Related Tags */}
            {song.relatedTags && Object.keys(song.relatedTags).length > 0 && (
                <p className="text-gray-600 mb-2">
                    <img src="/emoji/1F517.svg" alt="Related" className="inline w-5 h-5 mr-1 align-text-bottom" />
                    Related tags:
                    <span className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(song.relatedTags)
                            .sort((a, b) => b[1] - a[1]) // Sort by count (descending)
                            .slice(0, 8) // Limit to top 8 related tags
                            .map(([tag, count]) => (
                                <Link
                                    key={tag}
                                    to={`/tags/${encodeURIComponent(tag)}`}
                                    className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md text-sm hover:bg-gray-200"
                                >
                                    {tag} <span className="text-xs ml-1 text-gray-500">({count})</span>
                                </Link>
                            ))}
                    </span>
                </p>
            )}
            
            {/* Song length */}
            {song.song.length != null && (
                <p className="text-gray-600 mb-2">
                    <img src="/emoji/23F1.svg" alt="Length" className="inline w-5 h-5 mr-1 align-text-bottom" />
                    Length: {Math.floor(song.song.length / 60)}:{(song.song.length % 60).toString().padStart(2, '0')}
                </p>
            )}
            {/* Preview link for logged in users */}
            {user && (
                <div className="mb-2">
                    <button
                        type="button"
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 text-sm font-medium disabled:opacity-50"
                        onClick={async e => {
                            e.preventDefault();
                            if (previewLoading || previewUrl) return;
                            setPreviewLoading(true);
                            const url = `/api/song/${id}/preview`;
                            try {
                                const res = await fetch(url, {
                                    headers: { Authorization: `Bearer ${authToken}` }
                                });
                                if (res.status === 429) {
                                    const data = await res.json().catch(() => ({}));
                                    toast.error(data?.error || '⏱️ You’re previewing too fast. Wait a moment.');
                                    return;
                                }
                                if (!res.ok) throw new Error('Failed to fetch preview');
                                const blob = await res.blob();
                                if (previewUrl) {
                                    window.URL.revokeObjectURL(previewUrl);
                                }
                                const blobUrl = window.URL.createObjectURL(blob);
                                setPreviewUrl(blobUrl);
                                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10 * 60 * 1000); // Revoke after 10 min
                            } catch {
                                toast.error('❌ Failed to fetch preview');
                            } finally {
                                setPreviewLoading(false);
                            }
                        }}
                        disabled={previewLoading || !!previewUrl}
                    >
                        <img src="/emoji/1F50A.svg" alt="Preview" className="inline w-4 h-4 mr-1 align-text-bottom" />
                        {previewLoading ? 'Loading…' : 'Preview'}
                    </button>
                    {previewUrl && !previewLoading && (
                        <div className="mt-2">
                            <audio ref={audioRef} src={previewUrl} controls autoPlay style={{ width: '100%' }} type="audio/mp4" />
                        </div>
                    )}
                </div>
            )}

            <div className="mb-4 space-y-1">
                {avgVote !== null && (
                    <p>
                        <img src="/emoji/2B50.svg" alt="Average vote" className="inline w-5 h-5 mr-1 align-text-bottom" />
                        Average vote: {avgVote} / 5
                    </p>
                )}
                <Votestars
                    value={userVote || 0}
                    onVote={handleVote}
                    avg={avgVote}
                    songId={Number(id)}
                />
                <p>
                    <img src="/emoji/E260.svg" alt="Requests" className="inline w-5 h-5 mr-1 align-text-bottom" />
                    Requests: {song.history.length}
                </p>
            </div>

            {/* Request Song */}
            <div className="mb-4">
                <button
                    onClick={requestSong}
                    disabled={requesting || song.recently_played || queuedNow}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                    {requesting ? 'Requesting…' : 'Request this song'}
                </button>
                {(song.recently_played || queuedNow) && (
                    <p className="text-sm text-yellow-700 mt-1">
                        <img src="/emoji/1F512.svg" alt="Locked" className="inline w-4 h-4 mr-1 align-text-bottom" />
                        This song can’t be queued again just yet.
                    </p>
                )}
            </div>

            {/* Votes */}
            <div className="mt-4">
                <button onClick={() => setShowVotes(!showVotes)} className="text-blue-500 underline">
                    {showVotes ? 'Hide' : 'Show'} votes
                </button>
                {showVotes && (
                    <ul className="mt-2 space-y-1">
                        {song.votes.map((v, i) => {
                            const isFavorited = song.favorites.some((f) => f.username === v.username);
                            return (
                                <li key={i}>
                                    {v.username}{' '}
                                    {isFavorited && (
                                        <img src="/emoji/2764.svg" alt="Favorited" className="inline w-4 h-4 align-text-bottom" />
                                    )}
                                    : {v.vote}{' '}
                                    <img src="/emoji/2B50.svg" alt="Vote" className="inline w-4 h-4 align-text-bottom" />
                                    {user?.username === v.username && (
                                        <button onClick={removeVote} className="ml-2 text-red-500 hover:underline text-sm">
                                            Remove
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Request History */}
            <div className="mt-4">
                <button onClick={() => setShowRequests(!showRequests)} className="text-blue-500 underline">
                    {showRequests ? 'Hide' : 'Show'} request history
                </button>
                {showRequests && (
                    <ul className="mt-2 space-y-1">
                        {song.history.map((h, i) => (
                            <li key={i}>
                                {new Date(h.played_at).toLocaleString()} — {h.requested_by_username == null ? 'DJ Hypr' : h.requested_by_username}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Song tweaks for helpers */}
            <SongTweaks song={song.song} />

            {/* Comment box */}
            <div className="mt-6">
                <h2 className="font-semibold mb-2">Add a comment</h2>
                <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="w-full border p-2 rounded"
                    rows={3}
                />
                <button
                    onClick={submitComment}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Submit
                </button>
            </div>

            {/* Comments list */}
            <div className="mt-6">
                <h2 className="font-semibold mb-2">Comments</h2>
                {song.comments.length === 0 ? (
                    <p>No comments yet.</p>
                ) : (
                    <ul className="space-y-4">
                        {song.comments.map((c, i) => (
                            <li key={i} className="border-b pb-2">
                                <p className="text-sm text-gray-600">
                                    {c.username} — {new Date(c.created_at).toLocaleString()}
                                </p>
                                <p>
                                    {c.comment.split('\n').map((line, i) => (
                                        <React.Fragment key={i}>
                                            {line}
                                            {i < c.comment.split('\n').length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
